import Lottie from "lottie-react";
import loadingAnimation from "@/betby/assets/animation/loading.json";
import loadingLogo from "@/betby/assets/image/loading-logo.png";
import { ChevronLeft } from "lucide-react";
import { useLoadingViewModel } from "@/betby/viewmodels/useLoadingViewModel";

interface LoadingProps {
  /**
   * Whether to show the loading animation
   * @default true
   */
  visible?: boolean;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Background color
   * @default "rgba(0, 0, 0, 1)"
   */
  overlayColor?: string;
}

/**
 * Loading Component
 * Refactored with Back button, Logo, and Progress Bar with Lottie animation
 */
export default function Loading({
  visible = true,
  className = "",
  overlayColor = "rgba(0, 0, 0, 1)",
}: LoadingProps) {
  const { handleBack, progress } = useLoadingViewModel(visible);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-1001 flex flex-col items-center justify-between ${className}`}
      style={{ backgroundColor: overlayColor }}
    >
      {/* Top Left: Back Button */}
      <div className="absolute top-3 left-3 z-10">
        <button
          onClick={handleBack}
          className="p-2 text-white bg-black/10 rounded-full transition-colors active:scale-95"
          aria-label="Go back"
        >
          <ChevronLeft size={32} />
        </button>
      </div>

      {/* Center: Logo Image */}
      <div className="flex-1 flex items-center justify-center w-full px-10">
        <img
          src={loadingLogo}
          alt="Sports Logo"
          className="object-contain max-w-[80%] max-h-[40vh]"
        />
      </div>

      {/* Bottom: Progress Bar */}
      <div className="w-full px-8 pb-16 md:pb-24 max-w-2xl mb-8">
        <div className="relative w-full h-4 bg-white/20 rounded-full shadow-[inset_-1px_0px_2px_0px_rgba(255,255,255,0.5),0px_2px_2px_0px_rgba(0,0,0,0.25)]">
          {/* Progress Fill */}
          <div
            className="absolute top-0 left-0 h-full bg-linear-to-r from-[#3b82f6] to-[#93c5fd] rounded-full shadow-[0_0_15px_rgba(59,130,246,0.8)]"
            style={{ width: `${progress}%` }}
          />

          {/* Lottie Animation at the tip of the progress bar */}
          <div
            className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
            style={{
              left: `${progress}%`,
              transform: "translate(-75%, -20%)", // Center the Lottie on the tip
              width: "150px", // Adjust size for the soccer ball/light effect
              height: "150px",
            }}
          >
            <Lottie
              animationData={loadingAnimation}
              loop={true}
              autoplay={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
